import type { CSSProperties, ReactNode, RefObject } from "react";

import type { AnimatedModalContent } from "@/components/adg4/animated-modal/src";

export type NodeShape =
    | "circle"
    | "pill"
    | "rectangle"
    | "hexagon"
    | "triangle";

export type ConnectorShape =
    | "arced"
    | "straight"
    | "curvy"
    | "orthogonal"
    | "mixed";

export type ConnectorType = "solid" | "dashed";

export type ConnectorBias = "positive" | "negative";

export type AnimationColor = "lime" | "purple" | "blue" | "yellow" | "neutral";

export const usableColors = [
    "lime100",
    "lime200",
    "lime300",
    "lime400",
    "lime500",
    "lime600",
    "lime700",
    "lime800",
    "lime900",
    "purple100",
    "purple200",
    "purple300",
    "purple400",
    "purple500",
    "purple600",
    "purple700",
    "purple800",
    "purple900",
    "blue100",
    "blue200",
    "blue300",
    "blue400",
    "blue500",
    "blue600",
    "blue700",
    "blue800",
    "blue900",
    "yellow100",
    "yellow200",
    "yellow300",
    "yellow400",
    "yellow500",
    "yellow600",
    "yellow700",
    "yellow800",
    "yellow900",
    "neutral100",
    "neutral200",
    "neutral300",
    "neutral400",
    "neutral500",
    "neutral600",
    "neutral700",
    "neutral800",
    "neutral900",
    "orange100",
    "orange200",
    "orange300",
    "orange400",
    "orange500",
    "orange600",
    "orange700",
    "orange800",
    "orange900",
    "saffron100",
    "saffron200",
    "saffron300",
    "saffron400",
    "saffron500",
    "saffron600",
    "saffron700",
    "saffron800",
    "saffron900",
    "white",
    "black",
    "secondarynavy",
    "secondarygreen",
    "secondaryred",
    "transparent"
] as const;

export type FillColor = (typeof usableColors)[number];

export type SpiderWebNodeBorderColor = FillColor | "rainbowRovo";

export type { AnimatedModalContent as ModalContent } from "@/components/adg4/animated-modal/src";

export type OverlapFill =
    | FillColor
    | {
          color?: FillColor;
          backgroundImage?: string;
      };

export type WebNodeOverlapConfig = {
    elementIdToOverlap: string;
    color?: FillColor;
    backgroundImage?: string;
};

export const ANIMATION_PHASES_SCROLL_TIMELINE_UNITS_DEFAULT = 100;

export type AnimationPhasesDrive = "time" | "scroll";

export type AnimationPhase = {
    delay: number;
    duration: number;
    x?: number;
    y?: number;
    opacity?: number;
    size?: number;
    width?: number;
    height?: number;
    scrollWindowStart?: number;
    scrollWindowEnd?: number;
};

export type WebNodeConfig = {
    id: string;
    x: number;
    y: number;
    shape: NodeShape;
    size?: number;
    width?: number;
    height?: number;
    borderRadius?: number;
    bgColor?: FillColor;
    borderColor?: SpiderWebNodeBorderColor;
    borderWidth?: number;
    bgImage?: string;
    fontFamily?: "charlie" | "monospace";
    fontWeight?: CSSProperties["fontWeight"];
    text?: string;
    htmlContent?: ReactNode;
    textColor?: FillColor;
    opacity?: number;
    rotate?: number;
    mixBlendMode?: CSSProperties["mixBlendMode"];
    modalContent?: AnimatedModalContent;
    overlapColors?: WebNodeOverlapConfig[];
    animationPhases?: AnimationPhase[];
};

export type WebConnectorConfig = {
    from: string;
    to: string;
    connectorShape?: ConnectorShape;
    connectorType?: ConnectorType;
    connectorBias?: ConnectorBias;
    isAnimated?: boolean;
    color?: FillColor;
    animationColor?: AnimationColor;
};

export type ResolvedConnector = {
    from: WebNodeConfig;
    to: WebNodeConfig;
    connectorShape: ConnectorShape;
    connectorType: ConnectorType;
    index: number;
    connectorBias?: ConnectorBias;
    isAnimated?: boolean;
    color?: FillColor;
    animationColor?: AnimationColor;
};

export type SpiderWebCanvasClickPct = { x: number; y: number };

export type SpiderWebNodeFocusVariant = "node-edit" | "connector";

export type SpiderWebNodeFocus = {
    nodeId: string;
    variant: SpiderWebNodeFocusVariant;
};

export type SpiderWebContainerProps = {
    nodes: WebNodeConfig[];
    /**
     * @deprecated Use `useViewportDimensions` instead.
     * Previously capped the container to `max-height: 100vh`.
     */
    maxHeightViewport?: boolean;
    /**
     * When `true`, the container fills `100vw × 100vh`, the SVG uses a fixed square
     * viewBox (`1000 × 1000`) with `preserveAspectRatio="none"`, and each node is
     * inverse-scaled so shapes stay proportional (circles stay circular, squares
     * stay square) regardless of viewport aspect ratio.
     * `width` / `height` props are ignored when this is active.
     */
    useViewportDimensions?: boolean;
    connectors?: WebConnectorConfig[];
    useOnlyExplicitConnectors?: boolean;
    connectorShape?: ConnectorShape;
    connectorType?: ConnectorType;
    bgColor?: FillColor;
    width?: number | string;
    height?: number | string;
    maxNeighbors?: number;
    connectorColor?: FillColor;
    connectorWidth?: number;
    fontFamily?: "charlie" | "monospace";
    animateOnLoad?: boolean;
    loadEntranceSpeedMultiplier?: number;
    replayOnVisible?: boolean;
    showAnimationPauseControl?: boolean;
    animationPhasesDrive?: AnimationPhasesDrive;
    scrollSectionRef?: RefObject<HTMLElement | null>;
    onSvgCanvasClick?: (pct: SpiderWebCanvasClickPct) => void;
    onNodeClick?: (node: WebNodeConfig) => void;
    focusedConnector?: { from: string; to: string };
    nodeFocus?: SpiderWebNodeFocus;
};
